import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, DollarSign } from "lucide-react";

interface CommissionRule {
  id: string;
  name: string;
  description: string | null;
  basis: string;
  trigger_event: string;
  rate_percent: number | null;
  flat_amount: number | null;
  is_active: boolean;
}

const BASIS_OPTIONS = [
  { value: "percent_gross", label: "% of Gross Sale" },
  { value: "percent_gross_profit", label: "% of Gross Profit" },
  { value: "tiered", label: "Tiered by Volume" },
  { value: "flat", label: "Flat $ per Job" },
  { value: "hybrid", label: "Hybrid (% + Flat)" },
];

const TRIGGER_OPTIONS = [
  { value: "signed", label: "On Contract Signed" },
  { value: "deposit", label: "On Deposit Received" },
  { value: "completed", label: "On Job Completion" },
  { value: "paid", label: "On Full Payment" },
];

export function CommissionRulesPanel({ orgId }: { orgId: string }) {
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionRule | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    basis: "percent_gross",
    trigger_event: "signed",
    rate_percent: 0,
    flat_amount: 0,
    is_active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    if (orgId) load();
  }, [orgId]);

  const load = async () => {
    const { data, error } = await supabase
      .from("commission_rules")
      .select("*")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error loading rules", description: error.message, variant: "destructive" });
      return;
    }
    setRules(data as any || []);
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "",
      description: "",
      basis: "percent_gross",
      trigger_event: "signed",
      rate_percent: 0,
      flat_amount: 0,
      is_active: true,
    });
    setIsOpen(true);
  };

  const openEdit = (r: CommissionRule) => {
    setEditing(r);
    setForm({
      name: r.name,
      description: r.description || "",
      basis: r.basis,
      trigger_event: r.trigger_event,
      rate_percent: Number(r.rate_percent) || 0,
      flat_amount: Number(r.flat_amount) || 0,
      is_active: r.is_active,
    });
    setIsOpen(true);
  };

  const save = async () => {
    if (!form.name) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    const payload = {
      org_id: orgId,
      name: form.name,
      description: form.description || null,
      basis: form.basis as any,
      trigger_event: form.trigger_event as any,
      rate_percent: form.rate_percent,
      flat_amount: form.flat_amount,
      is_active: form.is_active,
    };
    const { error } = editing
      ? await supabase.from("commission_rules").update(payload).eq("id", editing.id)
      : await supabase.from("commission_rules").insert(payload);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: editing ? "Rule updated" : "Rule created" });
    setIsOpen(false);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this commission rule?")) return;
    const { error } = await supabase.from("commission_rules").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Rule deleted" });
    load();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Commission Rules
            </CardTitle>
            <CardDescription>Reusable commission structures for your team</CardDescription>
          </div>
          <Button onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" />
            New Rule
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {rules.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No commission rules yet. Create one to assign to employees.
          </p>
        ) : (
          <div className="space-y-3">
            {rules.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{r.name}</p>
                    {!r.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {BASIS_OPTIONS.find((b) => b.value === r.basis)?.label} •{" "}
                    {TRIGGER_OPTIONS.find((t) => t.value === r.trigger_event)?.label}
                    {r.rate_percent ? ` • ${r.rate_percent}%` : ""}
                    {r.flat_amount ? ` • $${r.flat_amount}` : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(r.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Rule" : "New Commission Rule"}</DialogTitle>
              <DialogDescription>Define how this commission is calculated and earned</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Standard Sales Rep 8%"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Basis</Label>
                  <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BASIS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Trigger</Label>
                  <Select value={form.trigger_event} onValueChange={(v) => setForm({ ...form, trigger_event: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRIGGER_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rate %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.rate_percent}
                    onChange={(e) => setForm({ ...form, rate_percent: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Flat $</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.flat_amount}
                    onChange={(e) => setForm({ ...form, flat_amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} />
              </div>
              <Button onClick={save} className="w-full">
                {editing ? "Save Changes" : "Create Rule"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
